import { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { MessageSquare, Star, EyeOff, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Universal comments panel. Works for any entity in the COMMENTABLE set on the backend:
 * project, task, procurement, site_visit, approval, ticket, payment, drawing
 */
export default function Comments({ entity, parentId, className = "" }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!parentId) return;
    try {
      const { data } = await api.get(`/comments/${entity}/${parentId}`);
      setComments(data);
    } catch (e) { /* silent */ }
  }, [entity, parentId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/comments/${entity}/${parentId}`, {
        text, is_private: isPrivate, is_important: isImportant,
      });
      setText(""); setIsImportant(false); setIsPrivate(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to post comment");
    } finally { setBusy(false); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid={`comments-${entity}`}>
      <div className="flex items-center gap-2 text-sm">
        <MessageSquare className="h-4 w-4 text-primary" />
        <span className="font-semibold">Comments</span>
        <span className="text-xs text-muted-foreground">({comments.length})</span>
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {comments.length === 0 && (
          <div className="text-xs text-muted-foreground italic py-2">No comments yet.</div>
        )}
        {comments.map((c) => (
          <div key={c.id}
               className={`p-2.5 rounded border text-sm ${c.is_important ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-secondary/30"}`}
               data-testid={`comment-${c.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold">{c.author_name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{new Date(c.at_iso).toLocaleString("en-IN")}</span>
                {c.is_important && <span className="text-[10px] uppercase tracking-wider text-amber-300 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" />important</span>}
                {c.is_private && <span className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-0.5"><EyeOff className="h-2.5 w-2.5" />private</span>}
              </div>
              {(user?.role === "admin" || c.author_id === user?.id) && (
                <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-red-400 transition-colors" title="Delete">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="whitespace-pre-wrap">{c.text}</div>
          </div>
        ))}
      </div>

      <div className="space-y-2 pt-2 border-t border-border">
        <Textarea value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Add a comment or note…" rows={2}
                  className="bg-secondary" data-testid={`comment-input-${entity}`}
                  onKeyDown={(e) => { if (e.ctrlKey && e.key === "Enter") submit(); }} />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={isImportant} onChange={(e) => setIsImportant(e.target.checked)} />
              <Star className={`h-3 w-3 ${isImportant ? "text-amber-400" : ""}`} />Important
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
              <EyeOff className="h-3 w-3" />Private
            </label>
          </div>
          <Button size="sm" onClick={submit} disabled={busy || !text.trim()} data-testid={`comment-submit-${entity}`}>
            <Send className="h-3.5 w-3.5 mr-1" />Post
          </Button>
        </div>
        <div className="text-[10px] text-muted-foreground">Tip: Ctrl+Enter to send. Private notes are visible only to admin and you.</div>
      </div>
    </div>
  );
}
